import React, { useState, useEffect } from 'react'
import { ThemeProvider, CssBaseline, Box, Container, Grid, Typography, CircularProgress, Drawer, IconButton, AppBar, Toolbar, useMediaQuery, useTheme, Menu, MenuItem, Divider, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { ConversationsProvider } from './contexts/ConversationsContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import ConversationsList from './components/ConversationsList'
import MessageThread from './components/MessageThread'
import MessageTemplates from './components/MessageTemplates'
import theme from './theme'
import AuthScreen from './components/Auth/AuthScreen'
import UserProfile from './components/Auth/UserProfile'
import Header from './components/Header'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import ChatIcon from '@mui/icons-material/Chat'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import api from './services/api'
import Settings from './components/Settings'
import FortnoxIntegration from './components/FortnoxIntegration'
import { useLocation } from 'react-router-dom'

function Main() {
  const { user, loading, signOut, phoneVerificationInProgress } = useAuth()
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [selectedConversationId, setSelectedConversationId] = useState(null)
  const [activeTab, setActiveTab] = useState('conversations')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const location = useLocation();
  const [showSettingsAlert, setShowSettingsAlert] = useState(false)

  // Effect to check URL params for active_tab
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('active_tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Add settings check function
  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      const settings = response.data;
      const name = settings.name || '';
      const companyName = settings.company?.name || '';
      const reviewLink = settings.company?.reviewLink || '';
      
      // Check if any required settings are missing
      return {
        complete: !!(name && companyName && reviewLink),
        settings: {
          name,
          companyName,
          reviewLink
        }
      };
    } catch (err) {
      console.error('Error checking user settings:', err);
      return { complete: false, settings: {} };
    }
  }

  const handleSelectConversation = (conversationId) => {
    setSelectedConversationId(conversationId)
    if (isMobile) {
      setDrawerOpen(false)
    }
  }

  const handleBackClick = () => {
    setSelectedConversationId(null)
    if (isMobile) {
      setDrawerOpen(true)
    }
  }

  const handleLogout = async () => {
    await signOut()
    handleCloseMobileMenu()
  }
  
  const handleTabChange = async (tab) => {
    // If changing to fortnox tab, check settings first
    if (tab === 'fortnox') {
      const { complete } = await fetchSettings();
      
      if (!complete) {
        // Settings incomplete, show alert instead of changing tab
        setShowSettingsAlert(true);
        return;
      }
    }
    
    setActiveTab(tab)
    setSelectedConversationId(null)
    if (tab === 'templates') {
      setSelectedConversationId(null)
    }
  }

  const handleCloseSettingsAlert = () => {
    setShowSettingsAlert(false);
  }
  
  const goToSettings = () => {
    setActiveTab('settings');
    setShowSettingsAlert(false);
  }

  const handleOpenMobileMenu = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };
  
  const handleCloseMobileMenu = () => {
    setMobileMenuAnchor(null);
  };
  
  const handleMobileMenuSelect = async (tab) => {
    // If selecting fortnox from mobile menu, check settings first
    if (tab === 'fortnox') {
      const { complete } = await fetchSettings();
      
      if (!complete) {
        // Settings incomplete, show alert
        handleCloseMobileMenu();
        setShowSettingsAlert(true);
        return;
      }
    }
    
    handleTabChange(tab);
    handleCloseMobileMenu();
    
    if (tab === 'templates') {
      setSelectedConversationId(null);
    }
  };

  // Effect to fetch the selected conversation data
  useEffect(() => {
    if (selectedConversationId && user) {
      // Fetch the conversation data from API
      const fetchConversation = async () => {
        try {
          const response = await api.get(`/users/conversations/${selectedConversationId}`);
          setSelectedConversation(response.data);
        } catch (error) {
          console.error('Error fetching conversation:', error);
          setSelectedConversation(null);
        }
      };
      
      fetchConversation();
    } else {
      setSelectedConversation(null);
    }
  }, [selectedConversationId, user]);

  // Listen for tab change events from other components
  useEffect(() => {
    const handleTabChangeEvent = (event) => {
      if (event.detail && event.detail.tab) {
        handleTabChange(event.detail.tab);
      }
    };
    
    window.addEventListener('app:changeTab', handleTabChangeEvent);
    
    return () => {
      window.removeEventListener('app:changeTab', handleTabChangeEvent);
    };
  }, [handleTabChange]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          flexDirection: 'column'
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Laddar...
        </Typography>
      </Box>
    )
  }

  if (!user) {
    return <AuthScreen onSuccess={() => console.log('Auth success')} />
  }

  // For mobile view
  const drawerContent = (
    activeTab === 'conversations' ? (
      <ConversationsList 
        onSelectConversation={handleSelectConversation}
        selectedConversationId={selectedConversationId}
      />
    ) : null
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header 
        activeTab={activeTab} 
        onTabChange={handleTabChange}
        isMobile={isMobile}
        onMenuClick={() => setDrawerOpen(!drawerOpen)}
        onMobileMenuClick={handleOpenMobileMenu}
        mobileMenuAnchor={mobileMenuAnchor}
        onMobileMenuClose={handleCloseMobileMenu}
        onMobileMenuSelect={handleMobileMenuSelect}
        onLogout={handleLogout}
      />
      
      {/* Settings Alert Dialog */}
      <Dialog
        open={showSettingsAlert}
        onClose={handleCloseSettingsAlert}
        aria-labelledby="settings-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="settings-dialog-title">
          Inställningar saknas
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Du behöver komplettera dina inställningar innan du kan använda Fortnox-integrationen.
          </Alert>
          <Typography variant="body1">
            För att kunna använda Fortnox-integrationen behöver du ange:
          </Typography>
          <ul>
            <li>Ditt namn</li>
            <li>Företagets namn</li>
            <li>Länk till recensionssida</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettingsAlert} color="inherit">
            Avbryt
          </Button>
          <Button onClick={goToSettings} variant="contained" color="primary">
            Gå till inställningar
          </Button>
        </DialogActions>
      </Dialog>
      
      <Box 
        sx={{ 
          display: 'flex', 
          flexGrow: 1,
          height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)', md: 'calc(100% - 64px)' },
          overflow: 'hidden'
        }}
      >
        {isMobile ? (
          <>
            <Drawer
              variant="temporary"
              open={drawerOpen || (!selectedConversationId && activeTab === 'conversations')}
              onClose={() => setDrawerOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: '100%',
                  height: '100%'
                },
                '& .MuiBackdrop-root': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              {drawerContent}
            </Drawer>
            
            <Box 
              sx={{ 
                flexGrow: 1, 
                width: '100%',
                display: (selectedConversationId || activeTab === 'templates' || activeTab === 'settings' || activeTab === 'fortnox') ? 'block' : 'none'
              }}
            >
              {activeTab === 'templates' ? (
                <MessageTemplates />
              ) : activeTab === 'settings' ? (
                <Settings />
              ) : activeTab === 'fortnox' ? (
                <Container maxWidth="md" sx={{ py: 3 }}>
                  <FortnoxIntegration />
                </Container>
              ) : (
                <MessageThread 
                  conversation={selectedConversation} 
                  onBack={handleBackClick}
                  isMobile={isMobile}
                />
              )}
            </Box>
          </>
        ) : (
          <>
            {activeTab === 'conversations' && (
              <Box 
                sx={{ 
                  width: '350px', 
                  flexShrink: 0,
                  borderRight: 1,
                  borderColor: 'divider',
                  height: '100%',
                  overflow: 'auto',
                }}
              >
                {drawerContent}
              </Box>
            )}
            
            <Box 
              sx={{ 
                flexGrow: 1, 
                width: activeTab === 'conversations' ? 'calc(100% - 350px)' : '100%',
                height: '100%',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {activeTab === 'templates' ? (
                <MessageTemplates />
              ) : activeTab === 'settings' ? (
                <Settings />
              ) : activeTab === 'fortnox' ? (
                <Container maxWidth="md" sx={{ py: 3 }}>
                  <FortnoxIntegration />
                </Container>
              ) : (
                <>
                  {selectedConversationId ? (
                    <MessageThread 
                      conversation={selectedConversation} 
                      isMobile={isMobile}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        flexDirection: 'column',
                        p: 3
                      }}
                    >
                      <Typography variant="h6" color="text.secondary" textAlign="center">
                        Välj en konversation från listan för att se meddelanden
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ConversationsProvider>
          <Main />
        </ConversationsProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App 